import {
  DashboardIcon,
  UsersIcon,
  ApplicationsIcon,
  PatientsIcon,
  PayrollIcon,
  EvaluationIcon,
  DocumentsIcon,
  InvoiceIcon,
  ReportsIcon,
  FormsIcon,
  SurveyorIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ReceiptIcon,
  LibraryIcon,
} from "../Icons";
import { canAccessRoute, roleCode } from "../utils/moduleAccess";

/** All sidebar entries — labels unchanged; order is per role group below. */
export const SIDEBAR_MENU_CATALOG = {
  dashboard: { name: "Dashboard", path: "/", icon: DashboardIcon },
  employees: { name: "Employees", path: "/employees", icon: UsersIcon },
  applications: { name: "Applications", path: "/applications", icon: ApplicationsIcon },
  documents: { name: "HR Documents", path: "/documents", icon: DocumentsIcon },
  patients: { name: "Patients", path: "/patients", icon: PatientsIcon },
  generalForms: { name: "General Forms", path: "/general-forms", icon: FormsIcon },
  qaCenter: {
    name: "QA Center",
    path: "/qa-center",
    icon: ShieldCheckIcon,
    comingSoon: true,
  },
  reports: { name: "Reports", path: "/reports", icon: ReportsIcon },
  billing: {
    name: "Billing",
    path: "/billing",
    icon: ReceiptIcon,
    comingSoon: true,
  },
  platformBilling: {
    name: "Plans",
    path: "/platform-billing",
    icon: PayrollIcon,
  },
  libraries: { name: "Libraries", path: "/libraries", icon: LibraryIcon },
  coverageArea: { name: "Coverage Area", path: "/coverage-area", icon: MapPinIcon },
  invoice: { name: "Invoice", path: "/invoice", icon: InvoiceIcon },
  payroll: { name: "Payroll", path: "/payroll", icon: PayrollIcon },
  evaluation: { name: "Evaluation", path: "/evaluation", icon: EvaluationIcon },
  surveyor: { name: "Surveyor", path: "/surveyor", icon: SurveyorIcon },
};

const p = (key) => SIDEBAR_MENU_CATALOG[key].path;

/** Explicit sidebar order per role group (paths). Access still enforced via ROUTE_PERMISSIONS. */
export const SIDEBAR_ORDER_BY_GROUP = {
  ADMIN: [
    p("dashboard"),
    p("employees"),
    p("applications"),
    p("documents"),
    p("patients"),
    p("generalForms"),
    p("qaCenter"),
    p("reports"),
    p("billing"),
    p("libraries"),
    p("coverageArea"),
    p("invoice"),
    p("payroll"),
    p("evaluation"),
    p("surveyor"),
  ],
  OA: [
    p("dashboard"),
    p("applications"),
    p("documents"),
    p("patients"),
    p("generalForms"),
    p("qaCenter"),
    p("billing"),
    p("libraries"),
    p("coverageArea"),
    p("invoice"),
    p("surveyor"),
  ],
  CG_CNA: [
    p("dashboard"),
    p("applications"),
    p("documents"),
    p("patients"),
    p("generalForms"),
    p("invoice"),
  ],
  CLINICAL: [
    p("applications"),
    p("documents"),
    p("patients"),
    p("generalForms"),
    p("invoice"),
  ],
  // Surveyor: HR Documents + Patients only.
  // /applications + /evaluation stay in ROUTE_PERMISSIONS for Documents deep-links.
  SRVY: [
    p("documents"),
    p("patients"),
  ],
  SUPERADMIN: [p("platformBilling")],
};

const CLINICAL_ROLE_CODES = new Set([
  "RN",
  "LVN",
  "PT",
  "PTA",
  "OT",
  "OTA",
  "ST",
  "MSW",
  "HHA",
  "CON",
]);

/** Map logged-in user → sidebar order group key. */
export const resolveSidebarRoleGroup = (user) => {
  const code = roleCode(user);
  if (code === "ADMIN") return "ADMIN";
  if (code === "SUPERADMIN") return "SUPERADMIN";
  if (code === "OA") return "OA";
  if (code === "CG" || code === "CNA") return "CG_CNA";
  if (code === "SRVY") return "SRVY";
  if (CLINICAL_ROLE_CODES.has(code)) return "CLINICAL";
  return "CLINICAL";
};

const catalogByPath = Object.fromEntries(
  Object.values(SIDEBAR_MENU_CATALOG).map((item) => [item.path, item]),
);

/**
 * Ordered, access-filtered sidebar menu for the current user.
 * Single entry point for Sidebar rendering.
 */
export const getSidebarMenuForUser = (user) => {
  if (!user) return [];
  const group = resolveSidebarRoleGroup(user);
  const order = SIDEBAR_ORDER_BY_GROUP[group] || SIDEBAR_ORDER_BY_GROUP.CLINICAL;
  return order
    .map((path) => catalogByPath[path])
    .filter((item) => item && canAccessRoute(user, item.path));
};
